import { z } from 'zod';
import { PRIORITY, TASK_STATUS } from '../constants.js';
import { dateOnlySchema, isoTimestampSchema, syncBookkeepingSchema, uuidSchema } from './base.js';

export const taskPrioritySchema = z.union([
  z.literal(PRIORITY.NONE),
  z.literal(PRIORITY.LOW),
  z.literal(PRIORITY.MID),
  z.literal(PRIORITY.HIGH),
]);

export const taskStatusSchema = z.union([
  z.literal(TASK_STATUS.OPEN),
  z.literal(TASK_STATUS.DONE),
  z.literal(TASK_STATUS.CANCELLED),
]);

// 周期性：完成后推进 dueDate 到下一次发生（Todoist 行为）
// freq: daily/weekly/monthly/yearly；interval: 每 N 个周期；byWeekday: weekly 指定周几（0=Sun..6=Sat）
// endDate: 可选的截止日（>=该日不再推进，最后一次完成将关闭 task）
export const recurrenceSchema = z.object({
  freq: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
  interval: z.number().int().min(1).max(365).default(1),
  byWeekday: z.array(z.number().int().min(0).max(6)).max(7).optional(),
  endDate: dateOnlySchema.nullable().optional(),
});
export type Recurrence = z.infer<typeof recurrenceSchema>;

// 日期形态：单日 (due_date) | 单时点 (due_at) | 日期区间 (start_date + end_date)
const dateShape = z
  .object({
    due_date: dateOnlySchema.nullable(),
    due_at: isoTimestampSchema.nullable(),
    start_date: dateOnlySchema.nullable(),
    end_date: dateOnlySchema.nullable(),
  })
  .superRefine((v, ctx) => {
    const modes = [
      v.due_date != null,
      v.due_at != null,
      v.start_date != null || v.end_date != null,
    ].filter(Boolean).length;
    if (modes > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Task must use only one of: due_date, due_at, or date range.',
      });
    }
    if ((v.start_date == null) !== (v.end_date == null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start_date and end_date must both be set or both be null.',
      });
    }
    if (v.start_date && v.end_date && v.start_date > v.end_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start_date must be <= end_date.',
      });
    }
  });

export const taskSchema = syncBookkeepingSchema
  .extend({
    id: uuidSchema,
    project_id: uuidSchema.nullable(),
    section_id: uuidSchema.nullable(),
    parent_task_id: uuidSchema.nullable(),
    title: z.string().min(1).max(500),
    description: z.string().nullable(),
    priority: taskPrioritySchema,
    status: taskStatusSchema,
    due_date: dateOnlySchema.nullable(),
    due_at: isoTimestampSchema.nullable(),
    start_date: dateOnlySchema.nullable(),
    end_date: dateOnlySchema.nullable(),
    completed_at: isoTimestampSchema.nullable(),
    recurrence: recurrenceSchema.nullable(),
    sort_key: z.string().min(1),
  })
  .superRefine((v, ctx) => {
    const result = dateShape.safeParse(v);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue(issue);
      }
    }
  });

export type Task = z.infer<typeof taskSchema>;

export const taskCreateSchema = z.object({
  id: uuidSchema,
  project_id: uuidSchema.nullable().optional(),
  section_id: uuidSchema.nullable().optional(),
  parent_task_id: uuidSchema.nullable().optional(),
  title: z.string().min(1).max(500),
  description: z.string().nullable().optional(),
  priority: taskPrioritySchema.default(PRIORITY.NONE),
  sort_key: z.string().min(1),
  due_date: dateOnlySchema.nullable().optional(),
  due_at: isoTimestampSchema.nullable().optional(),
  start_date: dateOnlySchema.nullable().optional(),
  end_date: dateOnlySchema.nullable().optional(),
  recurrence: recurrenceSchema.nullable().optional(),
});

export const taskPatchSchema = z
  .object({
    project_id: uuidSchema.nullable(),
    section_id: uuidSchema.nullable(),
    parent_task_id: uuidSchema.nullable(),
    title: z.string().min(1).max(500),
    description: z.string().nullable(),
    priority: taskPrioritySchema,
    status: taskStatusSchema,
    sort_key: z.string().min(1),
    due_date: dateOnlySchema.nullable(),
    due_at: isoTimestampSchema.nullable(),
    start_date: dateOnlySchema.nullable(),
    end_date: dateOnlySchema.nullable(),
    completed_at: isoTimestampSchema.nullable(),
    recurrence: recurrenceSchema.nullable(),
  })
  .partial();

export type TaskCreate = z.infer<typeof taskCreateSchema>;
export type TaskPatch = z.infer<typeof taskPatchSchema>;
